<template>
  <div v-if="selectedDocument">
    <v-row>
      <v-col>
        <h4>{{ $t('project.signing-checklist.pane.SigningChecklistPaneShareAttachment.selectedDocument') }}</h4>
      </v-col>
    </v-row>
    <v-row class="mt-0">
      <v-col>
        <v-chip variant="outlined">
          <ClosdFildersIcon class="mx-2" :document="selectedDocument" size="22"/>
          <span class="SigningChecklistAttachmentShare-filename">
            {{selectedDocument.basename}}
          </span>
        </v-chip>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-radio-group v-model="shareType"
                       class="mt-0"
                       hide-details
        >
          <v-radio :value="SHARE_TYPE_OPTIONS.PUBLIC"
                   class="attachment-share-radio"
                   :class="{ 'attachment-share-radio--active': shareType === SHARE_TYPE_OPTIONS.PUBLIC}"
          >
            <template #label>
              <div class="d-flex flex-column ml-2">
                <h3>{{ $t('project.signing-checklist.pane.SigningChecklistPaneShareAttachment.publicTitle') }}</h3>
                <div class="text-subtitle-1 text-grey-700">
                  {{ $t('project.signing-checklist.pane.SigningChecklistPaneShareAttachment.publicSubtitle') }}
                </div>
              </div>
            </template>
          </v-radio>
          <v-radio :value="SHARE_TYPE_OPTIONS.CUSTOM"
                   class="attachment-share-radio"
                   :class="{ 'attachment-share-radio--active': shareType === SHARE_TYPE_OPTIONS.CUSTOM}"
          >
            <template #label>
              <div class="d-flex flex-column ml-2">
                <h3>{{$t('project.signing-checklist.pane.SigningChecklistPaneShareAttachment.customTitle')}}</h3>
                <div class="text-subtitle-1 text-grey-700">
                  {{ $t('project.signing-checklist.pane.SigningChecklistPaneShareAttachment.customSubtitle') }}
                </div>
              </div>
            </template>
          </v-radio>
        </v-radio-group>
      </v-col>
    </v-row>

    <v-row v-if="pane.isOpen && shareType === SHARE_TYPE_OPTIONS.CUSTOM && groups"
           class="mt-0"
    >
      <v-col>
        <DocumentSharePicker :force-pm-selection="true" />
      </v-col>
    </v-row>

    <v-divider />

    <v-row>
      <v-col>
        <v-switch v-model="sendNotification"
                  hide-details
                  :label="$t('project.signing-checklist.pane.SigningChecklistPaneShareAttachment.sendNotification')"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <VisualTextEditor ref="notifyMessageInput"
                          v-model:value="notifyMessage"
                          counter="footer"
                          :error-messages="notifyMessageErrors"
                          :max-length="notifyMessageMaxLength"
                          :placeholder="$t('project.signing-checklist.pane.SigningChecklistPaneShareAttachment.optionalMessageWithCopy')"
        />
      </v-col>
    </v-row>

    <v-row class="mt-n2">
      <v-col>
        <AppButton color="primary"
                   variant="outlined"
                   block
                   @click="closePane"
        >
          {{$t('common.cancel')}}
        </AppButton>
      </v-col>
      <v-col>
        <AppButton color="primary"
                   block
                   :disabled="shareDisabled"
                   :loading="shareAttachmentPending"
                   @click="shareAttachmentFile"
        >
          {{$t('common.share')}}
        </AppButton>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'

import AppButton from '@/common/buttons/AppButton.vue'
import DocumentSharePicker from '@/common/document-share/DocumentSharePicker.vue'
import ClosdFildersIcon from '@/common/filders/ClosdFildersIcon.vue'
import { SHARE_TYPE_OPTIONS } from '@/common/utils/shareTypes'
import { escapeHtml } from '@/common/utils/strings'
import { toast } from '@/common/utils/toast'
import VisualTextEditor from '@/common/visual-text-editor/VisualTextEditor.vue'
import { DocumentSharePickerActionTypes } from '@/store/modules/document-share-picker/action_types'
import { CLOSE_PANE, SHARE_ATTACHMENT_FILE } from '@/store/modules/signing-checklist/action_types'

const NOTIFY_MESSAGE_MAX_LENGTH = 400

export default {
  name: 'SigningChecklistPaneShareAttachment',
  components: { AppButton, ClosdFildersIcon, DocumentSharePicker, VisualTextEditor },
  data () {
    return {
      notifyMessage: '',
      notifyMessageMaxLength: NOTIFY_MESSAGE_MAX_LENGTH,
      sendNotification: true,
      SHARE_TYPE_OPTIONS,
      shareTypeValue: '',
    }
  },
  computed: {
    ...mapState('groups', ['groups']),
    ...mapState('signingChecklist', ['shareAttachmentPending', 'pane']),
    notifyMessageLength () {
      return escapeHtml(this.notifyMessage).length
    },
    notifyMessageIsTooLong () {
      return this.notifyMessageLength > this.notifyMessageMaxLength
    },
    notifyMessageErrors () {
      const errors = []

      if (this.notifyMessageIsTooLong) {
        errors.push(this.$t('common.validations.textTooLong'))
      }

      return errors
    },
    selectedDocument () {
      if (!this.pane?.fileToShare) {
        return null
      }

      const splicedFilename = this.pane.fileToShare.name.split('.')
      const fileExt = splicedFilename.pop()

      return {
        basename: splicedFilename.join('.'),
        ext: fileExt,
      }
    },
    shareDisabled () {
      return this.notifyMessageIsTooLong
    },
    shareType: {
      get () {
        if (this.shareTypeValue) {
          return this.shareTypeValue
        }

        if (this.pane.fileToShare) {
          return this.pane.fileToShare.rights.read.isPublic
            ? SHARE_TYPE_OPTIONS.PUBLIC
            : SHARE_TYPE_OPTIONS.CUSTOM
        }

        return SHARE_TYPE_OPTIONS.CUSTOM
      },
      set (val) {
        this.shareTypeValue = val
        if (val === SHARE_TYPE_OPTIONS.CUSTOM) {
          this.INIT_SELECTED_GROUPS(this.pane.fileToShare.rights.read.groups)
          this.INIT_SELECTED_MEMBERS(this.pane.fileToShare.rights.read.users)
        }
      },
    },
  },
  watch: {
    'pane.isOpen' (isOpen) {
      if (isOpen) {
        this.sendNotification = true
        this.notifyMessage = ''

        if (this.pane.fileToShare?.rights) {
          this.INIT_SELECTED_GROUPS(this.pane.fileToShare.rights.read.groups)
          this.INIT_SELECTED_MEMBERS(this.pane.fileToShare.rights.read.users)
        }

        if (this.$refs.notifyMessageInput) {
          this.$refs.notifyMessageInput.clear()
        }
      }
    },
  },
  methods: {
    ...mapActions('signingChecklist', [CLOSE_PANE, SHARE_ATTACHMENT_FILE]),
    ...mapActions('documentSharePicker', [
      DocumentSharePickerActionTypes.INIT_SELECTED_GROUPS,
      DocumentSharePickerActionTypes.INIT_SELECTED_MEMBERS,
    ]),
    closePane () {
      this.CLOSE_PANE()
    },
    async shareAttachmentFile () {
      try {
        await this.SHARE_ATTACHMENT_FILE({
          notifyMessage: this.notifyMessage,
          sendNotification: this.sendNotification,
          shareType: this.shareType,
        })

        this.closePane()
        toast.success(this.$t('project.signing-checklist.pane.SigningChecklistPaneShareAttachment.shareAttachmentFileSuccess'))
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.signing-checklist.pane.SigningChecklistPaneShareAttachment.shareAttachmentFileError'))
      }
    },
  },
}
</script>

<style scoped lang="scss">
.attachment-share-radio {
  border-left: 7px solid transparent;
  margin-left: -20px;
  margin-right: -20px;
  padding-top: 14px;
  padding-bottom: 14px;

  &--active {
    border-left-color: rgb(var(--v-theme-primary));
    background-color: rgb(var(--v-theme-primary-lighten-4));
  }
}

.SigningChecklistAttachmentShare {
  &-filename {
    max-width: 350px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}
</style>
